/**
 * Admin utility to login as a specific user
 * This function creates a session for the specified user and navigates to the frontend app
 */

import {
  adminTokenHeader,
  clearAdminToken,
  isInvalidAdminTokenError,
} from "../auth/adminTokenStore";

const BASE_URL = process.env.REACT_APP_BE_URL as string;

interface LoginAsUserResponse {
  login_code: string;
}

/**
 * Gets the main Coverflow app URL (not the internal admin tool).
 * Uses REACT_APP_FRONTEND_APP_URL, falls back to https://app.coverflow.com.
 */
function getFrontendUrl(): string {
  return process.env.REACT_APP_FRONTEND_APP_URL || "https://app.coverflow.com";
}

async function loginAsUser(userId: string): Promise<LoginAsUserResponse | null> {
  try {
    // credentials: "include" sends the HttpOnly session cookie for SSO-authenticated admins
    const response = await fetch(`${BASE_URL}/admin-support-login`, {
      method: "POST",
      credentials: "include",
      headers: { ...adminTokenHeader(), "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });

    if (response.status !== 200) {
      // Read once: a body can only be consumed once, so a second read would log {}.
      const errorBody = await response.json().catch(() => ({}));
      if (isInvalidAdminTokenError(response.status, errorBody)) {
        clearAdminToken();
        return null;
      }
      console.error("Failed to login as user:", errorBody);
      return null;
    }

    const data: Partial<LoginAsUserResponse> = await response.json();
    if (!data.login_code) {
      console.error("Login-as response is missing login_code");
      return null;
    }
    return { login_code: data.login_code };
  } catch (error) {
    console.error("Error logging in as user:", error);
    return null;
  }
}

/**
 * Handles the complete login flow: creates session and opens frontend app.
 * Opens the window synchronously (before the async fetch) to avoid popup blockers.
 */
export async function handleAdminLoginAsUser(
  userId: string,
  frontendUrl?: string
): Promise<boolean> {
  let target: URL;
  try {
    target = new URL(frontendUrl || getFrontendUrl());
  } catch {
    // Checked before opening the tab or minting a code, so a bad config fails cleanly.
    console.error("Invalid app URL for login-as:", frontendUrl || getFrontendUrl());
    alert("Failed to login as user: the app URL is misconfigured.");
    return false;
  }

  // Open the window immediately within the user-gesture stack so browsers don't block it.
  const newWindow = window.open(target.toString(), "_blank");

  const loginData = await loginAsUser(userId);

  if (!loginData) {
    if (newWindow) newWindow.close();
    alert("Failed to login as user. Please try again.");
    return false;
  }

  const url = withLoginCode(target, loginData.login_code);

  if (newWindow) {
    newWindow.location.href = url;
  } else {
    // Fallback if popup was still blocked (e.g. non-user-gesture context)
    window.open(url, "_blank");
  }

  return true;
}

/**
 * Same-tab login-as for the /login-as deep link: no window.open, so it runs
 * on page load without a user gesture. `redirect` is an app path or a full URL
 * on the app's origin. Returns false if `redirect` points elsewhere or the mint failed.
 */
export async function loginAsUserAndRedirect(userId: string, redirect: string): Promise<boolean> {
  // Checked before minting, so a crafted link can't send a login code to another site.
  const url = resolveAppUrl(redirect);
  if (!url) {
    console.error("Refusing login-as redirect outside the app origin:", redirect);
    return false;
  }

  const loginData = await loginAsUser(userId);
  if (!loginData) return false;

  // replace, not href: the /login-as entry would otherwise sit in history, and
  // Back from the app would land on it and log in again.
  window.location.replace(withLoginCode(url, loginData.login_code));
  return true;
}

/**
 * Resolves `target` against the app URL; null unless it stays on the app's origin.
 * Also null when the app URL itself is misconfigured, so /login-as shows its error.
 */
export function resolveAppUrl(target: string): URL | null {
  try {
    const base = new URL(getFrontendUrl());
    const url = new URL(target, base);
    return url.origin === base.origin ? url : null;
  } catch {
    return null;
  }
}

// The URL carries only the one-time code, which the app redeems at
// /activate-session; the session and refresh tokens never touch a URL.
function withLoginCode(url: URL, loginCode: string): string {
  url.searchParams.set("login_code", loginCode);
  return url.toString();
}
